import React, { useState } from 'react';
import axios from 'axios';
import '../styles/TwitterTools.css';

const TwitterAccountIdFetcher = () => {
    const [mode, setMode] = useState('username-to-id'); // 'username-to-id' or 'id-to-username'
    const [username, setUsername] = useState('');
    const [userId, setUserId] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleFetch = async (e) => {
        e.preventDefault();
        setError('');
        setResult(null);
        setLoading(true);

        try {
            const payload = { mode };
            if (mode === 'username-to-id') {
                payload.username = username;
            } else {
                payload.userId = userId;
            }

            const response = await axios.post('/api/twitter-verification/fetch-user-id', payload);

            if (response.data.success) {
                setResult(response.data);
            }
        } catch (err) {
            console.error(err);
            if (err.response?.status === 402) {
                setError(`Insufficient credits. You need ${err.response.data.creditsNeeded} credits.`);
            } else if (err.response?.status === 401) {
                setError('Please login to use this tool.');
            } else {
                setError(err.response?.data?.message || 'Failed to fetch user information');
            }
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // You could add a toast notification here
    };

    return (
        <div className="twitter-tools-container">
            <div className="tools-header">
                <h1>🔍 Twitter Account ID Converter</h1>
                <p>Convert between Twitter username and numerical ID - works both ways!</p>
            </div>

            <div className="tools-form-container">
                {/* Mode Toggle */}
                <div className="mode-toggle" style={{
                    marginBottom: '1.5rem',
                    display: 'flex',
                    gap: '0.5rem',
                    justifyContent: 'center'
                }}>
                    <button
                        type="button"
                        onClick={() => {
                            setMode('username-to-id');
                            setError('');
                            setResult(null);
                        }}
                        style={{
                            padding: '0.75rem 1.5rem',
                            borderRadius: '8px',
                            border: '2px solid',
                            borderColor: mode === 'username-to-id' ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.2)',
                            background: mode === 'username-to-id' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                            color: mode === 'username-to-id' ? 'var(--primary-color)' : 'var(--text-secondary)',
                            cursor: 'pointer',
                            fontWeight: mode === 'username-to-id' ? '600' : '400',
                            transition: 'all 0.2s'
                        }}
                    >
                        📝 Username → ID
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setMode('id-to-username');
                            setError('');
                            setResult(null);
                        }}
                        style={{
                            padding: '0.75rem 1.5rem',
                            borderRadius: '8px',
                            border: '2px solid',
                            borderColor: mode === 'id-to-username' ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.2)',
                            background: mode === 'id-to-username' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                            color: mode === 'id-to-username' ? 'var(--primary-color)' : 'var(--text-secondary)',
                            cursor: 'pointer',
                            fontWeight: mode === 'id-to-username' ? '600' : '400',
                            transition: 'all 0.2s'
                        }}
                    >
                        🔢 ID → Username
                    </button>
                </div>

                <form onSubmit={handleFetch} className="tools-form">
                    {mode === 'username-to-id' ? (
                        <div className="form-group">
                            <label htmlFor="username">
                                Twitter Username *
                                <span className="label-hint">Without @ symbol</span>
                            </label>
                            <input
                                type="text"
                                id="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="elonmusk"
                                required
                            />
                        </div>
                    ) : (
                        <div className="form-group">
                            <label htmlFor="userId">
                                Twitter User ID *
                                <span className="label-hint">Numerical ID</span>
                            </label>
                            <input
                                type="text"
                                id="userId"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                placeholder="44196397"
                                required
                            />
                        </div>
                    )}

                    <button type="submit" className="tools-btn" disabled={loading}>
                        {loading ? 'Fetching...' : mode === 'username-to-id' ? 'Get User ID' : 'Get Username'}
                    </button>
                </form>

                <div className="info-notice" style={{
                    marginTop: '1.5rem',
                    padding: '1rem 1.5rem',
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '2px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                }}>
                    <span style={{ fontSize: '1.5rem' }}>⏱️</span>
                    <small style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                        <strong>Rate Limit Notice:</strong> API requests are optimized with 20 QPS.
                        Multiple requests can be processed quickly.
                    </small>
                </div>

                {error && (
                    <div className="error-message">
                        <span className="error-icon">⚠️</span>
                        {error}
                    </div>
                )}
            </div>

            {result && result.userInfo && (
                <div className="results-container">
                    <div className="results-section">
                        <h2>📊 Account Information</h2>

                        <div className="account-card">
                            {/* Profile Header with Banner */}
                            {result.userInfo.coverImageUrl && (
                                <div style={{
                                    width: '100%',
                                    height: '150px',
                                    background: `url(${result.userInfo.coverImageUrl}) center/cover`,
                                    borderRadius: '12px 12px 0 0',
                                    marginBottom: '60px'
                                }} />
                            )}
                            
                            <div className="account-header-info" style={{ marginTop: result.userInfo.coverImageUrl ? '-50px' : '0' }}>
                                {result.userInfo.profileImageUrl && (
                                    <img
                                        src={result.userInfo.profileImageUrl}
                                        alt={result.userInfo.displayName}
                                        className="profile-image"
                                        style={{
                                            border: '4px solid var(--card-bg)',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                        }}
                                    />
                                )}
                                <div className="account-details">
                                    <h3>
                                        {result.userInfo.displayName}
                                        {result.userInfo.isVerified && (
                                            <span className="verified-badge" title={`Verified Account${result.userInfo.verifiedType ? ` (${result.userInfo.verifiedType})` : ''}`}>✓</span>
                                        )}
                                        {result.userInfo.isAutomated && (
                                            <span style={{ marginLeft: '8px', fontSize: '0.9em', opacity: 0.7 }} title="Automated Account">🤖</span>
                                        )}
                                    </h3>
                                    <p className="username-display">@{result.userInfo.username}</p>
                                    
                                    {/* Bio */}
                                    {result.userInfo.bio && (
                                        <p style={{ 
                                            margin: '0.75rem 0',
                                            color: 'var(--text-primary)',
                                            lineHeight: '1.5'
                                        }}>
                                            {result.userInfo.bio}
                                        </p>
                                    )}

                                    {/* Location & Join Date */}
                                    <div style={{
                                        display: 'flex',
                                        gap: '1rem',
                                        flexWrap: 'wrap',
                                        marginTop: '0.75rem',
                                        fontSize: '0.9em',
                                        color: 'var(--text-secondary)'
                                    }}>
                                        {result.userInfo.location && (
                                            <span>📍 {result.userInfo.location}</span>
                                        )}
                                        {result.userInfo.createdAt && (
                                            <span>📅 Joined {new Date(result.userInfo.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                                        )}
                                        {result.userInfo.canDm && (
                                            <span title="Can receive Direct Messages">💬 DMs Open</span>
                                        )}
                                        {result.userInfo.url && (
                                            <a href={result.userInfo.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>
                                                🔗 {result.userInfo.url}
                                            </a>
                                        )}
                                    </div>

                                    {/* Stats Row */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                                        gap: '1rem',
                                        marginTop: '1rem',
                                        padding: '1rem',
                                        background: 'rgba(255,255,255,0.05)',
                                        borderRadius: '8px'
                                    }}>
                                        <div>
                                            <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>Followers</div>
                                            <div style={{ fontSize: '1.2em', fontWeight: '600', color: 'var(--primary-color)' }}>
                                                {result.userInfo.followers?.toLocaleString()}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>Following</div>
                                            <div style={{ fontSize: '1.2em', fontWeight: '600' }}>
                                                {result.userInfo.following?.toLocaleString()}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>Tweets</div>
                                            <div style={{ fontSize: '1.2em', fontWeight: '600' }}>
                                                {result.userInfo.tweets?.toLocaleString()}
                                            </div>
                                        </div>
                                        {result.userInfo.mediaCount > 0 && (
                                            <div>
                                                <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>Media</div>
                                                <div style={{ fontSize: '1.2em', fontWeight: '600' }}>
                                                    {result.userInfo.mediaCount?.toLocaleString()}
                                                </div>
                                            </div>
                                        )}
                                        {result.userInfo.favorites > 0 && (
                                            <div>
                                                <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>Likes</div>
                                                <div style={{ fontSize: '1.2em', fontWeight: '600' }}>
                                                    {result.userInfo.favorites?.toLocaleString()}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="id-display-section">
                                <div className="id-card">
                                    <div className="id-label">User ID</div>
                                    <div className="id-value">{result.userInfo.userId}</div>
                                    <button
                                        className="copy-btn"
                                        onClick={() => copyToClipboard(result.userInfo.userId)}
                                        title="Copy to clipboard"
                                    >
                                        📋 Copy ID
                                    </button>
                                </div>

                                <div className="id-card">
                                    <div className="id-label">Username</div>
                                    <div className="id-value">@{result.userInfo.username}</div>
                                    <button
                                        className="copy-btn"
                                        onClick={() => copyToClipboard(result.userInfo.username)}
                                        title="Copy to clipboard"
                                    >
                                        📋 Copy Username
                                    </button>
                                </div>
                            </div>

                            {/* Additional Profile Features */}
                            {(result.userInfo.pinnedTweetIds?.length > 0 || result.userInfo.hasCustomTimelines || result.userInfo.isTranslator) && (
                                <div style={{
                                    marginTop: '1rem',
                                    padding: '1rem',
                                    background: 'rgba(255,255,255,0.03)',
                                    borderRadius: '8px',
                                    fontSize: '0.9em'
                                }}>
                                    <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                                        Additional Features
                                    </div>
                                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                        {result.userInfo.pinnedTweetIds?.length > 0 && (
                                            <span>📌 {result.userInfo.pinnedTweetIds.length} Pinned Tweet{result.userInfo.pinnedTweetIds.length > 1 ? 's' : ''}</span>
                                        )}
                                        {result.userInfo.hasCustomTimelines && (
                                            <span>📋 Custom Timelines</span>
                                        )}
                                        {result.userInfo.isTranslator && (
                                            <span>🌐 Community Translator</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="results-footer">
                            <small>
                                ℹ️ This information is fetched from Twitter's public API.
                                <br />
                                Fetched on: {new Date(result.fetchedAt).toLocaleString()}
                            </small>
                        </div>
                    </div>
                </div>
            )}

            <div className="info-section">
                <h2>ℹ️ How to Use</h2>
                <div className="info-grid">
                    <div className="info-card">
                        <h3>🔍 What is a User ID?</h3>
                        <p>
                            Every Twitter account has a unique numerical ID that never changes, even if the username is changed.
                            This ID is permanent and useful for tracking accounts and API integrations.
                        </p>
                    </div>
                    <div className="info-card">
                        <h3>📝 How to Use</h3>
                        <p>
                            <strong>Username → ID:</strong> Enter a Twitter username (without @) to get the numerical ID.<br/>
                            <strong>ID → Username:</strong> Enter a numerical ID to get the current username.
                        </p>
                    </div>
                    <div className="info-card">
                        <h3>💡 Use Cases</h3>
                        <p>
                            User IDs are essential for API development, account tracking, bot development,
                            and ensuring you're always referencing the correct account even after username changes.
                        </p>
                    </div>
                    <div className="info-card" style={{
                        background: 'rgba(234, 179, 8, 0.1)',
                        border: '2px solid rgba(234, 179, 8, 0.3)'
                    }}>
                        <h3>⚠️ About Username History</h3>
                        <p>
                            Twitter/X does not provide a public API for viewing past usernames. The current username is shown,
                            but this tool cannot display historical username changes. The numerical ID remains constant forever,
                            making it the most reliable way to track accounts over time.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TwitterAccountIdFetcher;
